/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as React from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import Button from 'ui/atoms/Button'
import ChevronDownMenu from 'ui/atoms/ChevronDownMenu'
import Flex from 'ui/atoms/Flex'
import Space from 'ui/atoms/Space'
import Table from 'ui/atoms/Table'
import Checkbox from 'ui/Checkbox'
import ModalDialog from 'ui/ModalDialog'
import TextField from 'ui/molecules/TextField'
import Page from 'ui/organisms/Page'
import Select from 'ui/Select'

import googleSheetsLogo from '@/assets/google-sheets-logo.png'
import spinner from '@/assets/spinner.svg'
import { useLoadFormLabels } from '@/queries/forms/useLoadForms'
import { useAddGoogleSheetsSettings } from '@/queries/integrations/google_sheets/useAddGoogleSheetsSettings'
import { useDeleteGoogleSheetsSettings } from '@/queries/integrations/google_sheets/useDeleteGoogleSheetsSettings'
import { useLoadGoogleSheetsSettings } from '@/queries/integrations/google_sheets/useLoadGoogleSheetsSettings'
import { useUpdateGoogleSheetsSettings } from '@/queries/integrations/google_sheets/useUpdateGoogleSheetsSettings'

export const GoogleSheetsLogo = styled.img.attrs(() => ({
  src: googleSheetsLogo,
  alt: 'google-logo',
}))`
  width: 160px;
  display: inline-block;
`

const GoogleSheetsSettings = (): React.ReactNode => {
  const {
    data: sheetsSettings,
    isLoading: isListLoading,
    isFetching: isListFetching,
  } = useLoadGoogleSheetsSettings()
  const { addGoogleSheetsSetting, isLoading: isAddLoading } =
    useAddGoogleSheetsSettings()
  const { updateGoogleSheetsSetting, isLoading: isUpdateLoading } =
    useUpdateGoogleSheetsSettings()
  const { deleteGoogleSheetsSetting, isLoading: isDeleteLoading } =
    useDeleteGoogleSheetsSettings()
  const [selectedSetting, setSelectedSetting] = React.useState<{
    id?: string
    file_name: string
    form: string
    send_existing_responses: boolean
  } | null>(null)
  const { data: forms, isLoading: formsLoading } = useLoadFormLabels()

  const formOptions = React.useMemo(() => {
    return (
      forms?.map((form) => ({
        label: form.name,
        value: form.id,
      })) || []
    )
  }, [forms])

  const isLoading = isListLoading || isDeleteLoading || isListFetching

  return (
    <Page
      withBackButton
      helmetTitle="Google Sheets"
      titleComponent={<GoogleSheetsLogo />}
    >
      <Space>
        <Flex $horizontalSpacing="large">
          <Button
            $primary
            onClick={() =>
              setSelectedSetting({
                file_name: '',
                form: '',
                send_existing_responses: false,
              })
            }
          >
            Add
          </Button>
        </Flex>

        <Table>
          <Table.Head columns={['FILE NAME', 'FORM', '']} />

          <Table.Body>
            {isLoading && (
              <Table.Row>
                <Table.Column colspan={3}>Loading...</Table.Column>
              </Table.Row>
            )}

            {!isLoading && (sheetsSettings?.length || 0) === 0 && (
              <Table.Row>
                <Table.Column colspan={3}>No Results Found</Table.Column>
              </Table.Row>
            )}

            {!isLoading &&
              sheetsSettings?.map((setting) => (
                <Table.Row>
                  <Table.Column>{setting.file_name}</Table.Column>
                  <Table.Column>{setting.form.name}</Table.Column>
                  <Table.Column>
                    <ChevronDownMenu
                      options={[
                        {
                          label: 'Edit',
                          onClick: () => {
                            setSelectedSetting({
                              id: setting.id,
                              file_name: setting.file_name,
                              form: setting.form.id,
                              send_existing_responses:
                                setting.send_existing_responses,
                            })
                          },
                        },
                        {
                          label: 'Delete',
                          onClick: () => {
                            deleteGoogleSheetsSetting(setting.id)
                              .then(() => {
                                toast.success('Google integration deleted')
                              })
                              .catch(() => {
                                toast.error(
                                  'Failed to delete Google integration'
                                )
                              })
                          },
                        },
                      ]}
                    />
                  </Table.Column>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>
      </Space>
      {selectedSetting && (
        // @ts-ignore
        <ModalDialog
          opened
          footer={
            <div className="my-4 flex justify-end px-4 sm:px-6">
              <Button
                $primary
                disabled={isAddLoading || isUpdateLoading || formsLoading}
                onClick={async () => {
                  if (!selectedSetting) return
                  if (!selectedSetting.file_name) {
                    toast.error('Folder name is required')
                    return
                  }
                  if (!selectedSetting.form) {
                    toast.error('Form is required')
                    return
                  }

                  if (selectedSetting?.id) {
                    await updateGoogleSheetsSetting(
                      selectedSetting.id,
                      selectedSetting
                    )
                    toast.success('Google Sheets integration updated')
                  } else {
                    await addGoogleSheetsSetting(selectedSetting)
                    toast.success('Google Sheets integration created')
                  }
                  setSelectedSetting(null)
                }}
              >
                {(isAddLoading || isUpdateLoading) && (
                  <img alt="spinner" className="mr-2" src={spinner} />
                )}
                {selectedSetting?.id ? 'Save' : 'Create'}
              </Button>
            </div>
          }
          onClose={() => {
            setSelectedSetting(null)
          }}
          title={
            selectedSetting?.id
              ? 'Edit Google Sheets Integration'
              : 'Create Google Sheets Integration'
          }
        >
          <Space className="pt-2">
            <TextField
              semibold
              useEvent
              label="Enter File Name"
              onChange={(e) => {
                e.persist()
                setSelectedSetting((previous) => {
                  if (!previous) return null
                  return {
                    ...previous,
                    file_name: e.target.value,
                  }
                })
              }}
              value={selectedSetting?.file_name}
            />
            {/* @ts-ignore */}
            <Select
              showOptions
              showPlaceholder
              label="Send Form submissions to Google Sheets"
              menuPosition="fixed"
              onChange={(option: { value: string }) => {
                setSelectedSetting((previous) => {
                  if (!previous) return null
                  return {
                    ...previous,
                    form: option?.value || '',
                  }
                })
              }}
              options={formOptions}
              placeholder="-- Select Form --"
              value={formOptions.filter(
                (option) => selectedSetting?.form === option.value
              )}
            />

            {/* @ts-ignore */}
            <Checkbox
              willShowNotAllowedCursor
              checked={selectedSetting?.send_existing_responses}
              disabled={selectedSetting?.id}
              label="Send existing submissions to the sheet."
              onChange={(checked: boolean) => {
                setSelectedSetting((previous) => {
                  if (!previous) return null
                  return {
                    ...previous,
                    send_existing_responses: checked,
                  }
                })
              }}
            />
          </Space>
        </ModalDialog>
      )}
    </Page>
  )
}

export default GoogleSheetsSettings
